import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import {
  getActionItems,
  getScore,
  markActionItem,
  verifyActionItem,
} from "../../services/standards-service";
import {
  ActionItemType,
  IActionItems,
  LevelScore,
  mapLevel,
} from "../../types/action-item";
import { AppError } from "../../types/app";
import { useError } from "../../contexts/ErrorContext";
import { useParams } from "react-router";
import { useCallback, useEffect, useState } from "react";
import { useUserContext } from "../../contexts/user-context";
import AddIcon from "@mui/icons-material/Add";
import RejectIcon from "@mui/icons-material/Close";
import TickIcon from "@mui/icons-material/Check";
import { ActionsCard } from "../../components/ActionPlanCards";
import { ActionPlanHeader } from "../../components/ActionPlanHeader";
import { IStandards } from "../../types/standard";
import { useStandards } from "../../contexts/StandardsProvider";

export default function ActionPlanVerification() {
  const [standard, setStandard] = useState<IStandards | undefined>(undefined);
  const [actionItems, setActionItems] = useState<ActionItemType>({
    actionItemsCompleted: [],
    actionItemsToDo: [],
  });
  const [verifiedItems, setVerifiedItems] = useState<number[]>([]);
  const [levelsScore, setLevelsScore] = useState<LevelScore>({});

  const { id } = useParams();
  const { refreshData } = useStandards();
  const userContext = useUserContext();
  const { handleError } = useError();

  const getStandardScore = useCallback(async () => {
    if (id) {
      try {
        const eachStandard = await getScore(parseInt(id));
        setStandard(eachStandard);
        userContext.set({
          selectedStandard: eachStandard,
        });
      } catch (e) {
        const error = e as AppError;
        if (error && error.message) {
          handleError(error.message);
        }
      }
    }
  }, [id, userContext, handleError]);

  const getActionPlan = useCallback(async () => {
    if (id) {
      try {
        const actionItems = await getActionItems(parseInt(id));

        const previouslyVerifiedItems = actionItems.filter(
          (item) => item.verifiedAt != null,
        );
        setVerifiedItems(previouslyVerifiedItems.map((item) => item.id));

        const completedActionItem: IActionItems[] = [];
        const incompleteActionItem: IActionItems[] = [];
        const levelCounts: LevelScore = {};
        actionItems.forEach((eachActionItem) => {
          const { standardLevel } = eachActionItem;
          if (!levelCounts[mapLevel[standardLevel]]) {
            levelCounts[mapLevel[standardLevel]] = { total: 0, completed: 0 };
          }
          if (eachActionItem.done) {
            completedActionItem.push(eachActionItem);
            levelCounts[mapLevel[standardLevel]]!.completed += 1;
          } else {
            incompleteActionItem.push(eachActionItem);
          }
          levelCounts[mapLevel[standardLevel]]!.total += 1;
        });
        setActionItems({
          actionItemsCompleted: completedActionItem,
          actionItemsToDo: incompleteActionItem,
        });
        setLevelsScore(levelCounts);
      } catch (e) {
        const error = e as AppError;
        if (error && error.message) {
          handleError(error.message);
        }
      }
    }
  }, [id, handleError]);

  useEffect(() => {
    getStandardScore();
    getActionPlan();
    // eslint-disable-next-line
  }, [id]);

  const toggleActionItem = async (id: number, verify: boolean) => {
    try {
      if (!verifiedItems.includes(id)) {
        setVerifiedItems([...verifiedItems, id]);
      } else {
        setVerifiedItems(verifiedItems.filter((item) => item !== id));
      }
      await verifyActionItem(id, verify);
      //   getStandardScore();
      //   getActionPlan();
      //   refreshData();
    } catch (e) {
      const error = e as AppError;
      if (error && error.message) {
        handleError(error.message);
      }
    }
  };

  const rejectActionItem = async (id: number) => {
    try {
      await toggleActionItem(id, false);
      await markActionItem(id, false);
      getStandardScore();
      getActionPlan();
      refreshData();
    } catch (e) {
      const error = e as AppError;
      if (error && error.message) {
        handleError(error.message);
      }
    }
  };

  return (
    <Box component="main" sx={{ flex: 1 }}>
      <ActionPlanHeader
        standard={standard}
        levelsScore={levelsScore}
        getStandardScore={getStandardScore}
      />
      <Grid
        container
        className="action-item"
        sx={{
          padding: "20px",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Chip
            label="Verification Process"
            sx={{
              background: "#faaa0a",
              fontSize: "24px",
              paddingTop: "30px",
              paddingBottom: "30px",
            }}
            // size="medium"
          />
          <Box
            sx={{ width: "100%" }}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="h3"
              sx={{
                marginBottom: "20px",
                marginTop: "20px",
                fontSize: "24px",
              }}
            >
              Actions Marked Done ({actionItems.actionItemsCompleted.length})
            </Typography>
          </Box>

          {actionItems.actionItemsCompleted.map((actionItem, i) => (
            <ActionsCard
              actionItem={actionItem}
              isActionPlan={false}
              key={i}
              hideResources={true}
              ButtonComponent={
                <>
                  <Button
                    role="button"
                    variant="contained"
                    onClick={() =>
                      toggleActionItem(
                        actionItem.id,
                        verifiedItems.includes(actionItem.id) ? false : true,
                      )
                    }
                    sx={
                      verifiedItems.includes(actionItem.id)
                        ? {
                            borderRadius: "25px",
                            fontSize: "16px",
                            padding: "7px 36px",
                            background: "#2ad08dff",
                            boxShadow: "none",
                            color: "#fff",
                            marginBottom: "10px",
                            "&:hover": {
                              background: "#2ad08dff",
                              borderColor: "#6B7280",
                              boxShadow: "none",
                            },
                          }
                        : {
                            borderRadius: "25px",
                            fontSize: "16px",
                            padding: "7px 36px",
                            background: "#fff",
                            boxShadow: "none",
                            color: "#374151",
                            marginBottom: "10px",
                            "&:hover": {
                              background: "#E5E7EB",
                              borderColor: "#6B7280",
                              boxShadow: "none",
                            },
                          }
                    }
                    startIcon={
                      verifiedItems.includes(actionItem.id) ? (
                        <TickIcon />
                      ) : (
                        <AddIcon />
                      )
                    }
                  >
                    {verifiedItems.includes(actionItem.id)
                      ? "Verified"
                      : "Verify"}
                  </Button>

                  <Button
                    role="button"
                    variant="contained"
                    onClick={() => rejectActionItem(actionItem.id)}
                    sx={{
                      borderRadius: "25px",
                      fontSize: "16px",
                      padding: "7px 36px",
                      background: "#ffbcbcff",
                      boxShadow: "none",
                      color: "#374151",
                      marginBottom: "10px",
                      "&:hover": {
                        background: "#E5E7EB",
                        borderColor: "#6B7280",
                        boxShadow: "none",
                      },
                    }}
                    startIcon={<RejectIcon />}
                  >
                    Reject
                  </Button>
                </>
              }
            />
          ))}
        </Box>
      </Grid>
    </Box>
  );
}
